import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Footer from './Footer';
import { useT } from '../i18n/I18nContext';
import { trackEvent } from '../analytics';
import { useTrackSection } from '../hooks/useTrackSection';

export default function FinalCTASection() {
  const t = useT();
  const sectionRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  useTrackSection('final_cta', sectionRef);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        contentRef.current?.querySelectorAll('.fcta-animate') || [],
        { opacity: 0, y: 32 },
        {
          opacity: 1, y: 0, duration: 0.75, stagger: 0.1, ease: 'power2.out',
          scrollTrigger: { trigger: sectionRef.current, start: 'top 80%' },
        }
      );
    });
    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      id="cta-final"
      style={{
        position: 'relative',
        zIndex: 1,
        background: 'transparent',
      }}
    >
      {/* Final CTA block */}
      <div style={{
        padding: 'clamp(64px, 9vw, 120px) clamp(24px, 5vw, 80px)',
        textAlign: 'center',
      }}>
        <div style={{ maxWidth: 680, margin: '0 auto' }} ref={contentRef}>

          {/* Glow orb */}
          <div style={{
            position: 'absolute',
            left: '50%', top: '30%',
            transform: 'translate(-50%, -50%)',
            width: 360, height: 360,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(243,107,175,0.18), transparent 70%)',
            filter: 'blur(40px)',
            pointerEvents: 'none',
          }} />

          <div
            className="fcta-animate"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              background: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.22)',
              borderRadius: 100,
              padding: '7px 16px',
              marginBottom: 24,
              opacity: 0,
            }}
          >
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
              <path d="M8 1L10 6H15L11 9.5L12.5 15L8 12L3.5 15L5 9.5L1 6H6L8 1z" stroke="#F36BAF" strokeWidth="1.4" strokeLinejoin="round" fill="rgba(243,107,175,0.15)" />
            </svg>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.70)', letterSpacing: '0.10em', textTransform: 'uppercase' }}>
              {t.finalCta.eyebrow}
            </span>
          </div>

          <h2
            className="fcta-animate"
            style={{
              fontSize: 'clamp(26px, 3.2vw, 46px)',
              fontWeight: 800,
              lineHeight: 1.15,
              letterSpacing: '-0.022em',
              color: 'white',
              marginBottom: 18,
              opacity: 0,
            }}
          >
            {t.finalCta.titleA}<br />
            <span style={{ opacity: 0.72 }}>{t.finalCta.titleB}</span>
          </h2>

          <p
            className="fcta-animate"
            style={{
              fontSize: 'clamp(14px, 1.6vw, 17px)',
              color: 'rgba(255,255,255,0.62)',
              lineHeight: 1.65,
              marginBottom: 36,
              opacity: 0,
            }}
          >
            {t.finalCta.body.split('\n').map((line, i, arr) => (
              <React.Fragment key={i}>
                {line}
                {i < arr.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>

          <div className="fcta-animate" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 14, flexWrap: 'wrap', opacity: 0 }}>
            <Link
              to="/get-started"
              onClick={() => trackEvent('cta_clicked', { location: 'final_cta', label: 'primary' })}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 8,
                background: 'linear-gradient(135deg, #F36BAF 0%, #8B7CFF 100%)',
                color: 'white', borderRadius: 100, border: 'none',
                padding: '15px 32px', fontFamily: 'Manrope,sans-serif',
                fontWeight: 700, fontSize: 16, textDecoration: 'none',
                letterSpacing: '-0.01em',
                boxShadow: '0 8px 32px rgba(243,107,175,0.38)',
                transition: 'opacity 0.2s, transform 0.2s',
              }}
              onMouseEnter={e => { (e.currentTarget as HTMLElement).style.opacity = '0.88'; (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)'; }}
              onMouseLeave={e => { (e.currentTarget as HTMLElement).style.opacity = '1'; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
            >
              {t.finalCta.cta}
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8h10M9 4l4 4-4 4" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>

            <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
              <div style={{ display: 'flex' }}>
                {[...Array(5)].map((_, i) => (
                  <svg key={i} width="14" height="14" viewBox="0 0 14 14" fill="none">
                    <path d="M7 1l1.5 4H13l-3.5 2.5L11 12 7 9.5 3 12l1.5-4.5L1 5h4.5L7 1z" fill="#F36BAF" />
                  </svg>
                ))}
              </div>
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.55)', fontWeight: 500 }}>
                {t.finalCta.trust}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Shared Footer */}
      <Footer />
    </section>
  );
}
